import { motion, AnimatePresence } from "framer-motion";
import { XCircle, X, AlertTriangle } from "lucide-react";
import { useState } from "react";

export default function RejectionModal({ isOpen, onClose, policy, onReject }) {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleReject = async () => {
    if (!reason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      await onReject(policy.slug, reason);
      setReason("");
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setError("");
    onClose();
  };

  const quickReasons = [
    "Incomplete documentation",
    "Insufficient coverage details",
    "Non-compliant with regulations",
    "Pricing concerns",
    "Unclear terms and conditions",
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-50 backdrop-blur-sm bg-black/50"
          />

          {/* Modal */}
          <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="overflow-y-auto relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 z-10 p-1.5 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="p-4 sm:p-5">
                {/* Icon */}
                <div className="flex justify-center mb-3">
                  <div className="p-2.5 bg-red-100 rounded-full">
                    <XCircle className="w-10 h-10 text-red-600" />
                  </div>
                </div>

                {/* Title */}
                <h2 className="mb-1.5 text-xl sm:text-2xl font-bold text-center text-gray-900">
                  Reject Policy
                </h2>

                {/* Description */}
                <p className="mb-4 text-sm sm:text-base text-center text-gray-600">
                  Please provide a reason for rejecting this policy
                </p>

                {/* Policy Info */}
                <div className="p-3 mb-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Policy Name</p>
                      <p className="text-sm font-semibold text-gray-900 line-clamp-1">{policy?.policy_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Provider</p>
                      <p className="text-sm font-medium text-gray-900 line-clamp-1">{policy?.insurance_provider?.company_name || "N/A"}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="text-sm font-medium text-gray-900 capitalize">
                        {policy?.policy_type?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Reason Buttons */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs sm:text-sm font-medium text-gray-700">Quick Select:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {quickReasons.map((quickReason, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setReason(quickReason)}
                        className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                          reason === quickReason
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {quickReason}
                      </motion.button>
                    ))}
                  </div>
                </div>

                {/* Rejection Reason Textarea */}
                <div className="mb-3">
                  <label className="block mb-1.5 text-xs sm:text-sm font-medium text-gray-700">
                    Rejection Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      setError("");
                    }}
                    placeholder="Explain why this policy is being rejected..."
                    rows={3}
                    className={`w-full px-3 py-2 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 transition-colors ${
                      error
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                    }`}
                  />
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-1 text-xs text-red-600"
                    >
                      {error}
                    </motion.p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {reason.length} characters
                  </p>
                </div>

                {/* Warning */}
                <div className="flex gap-2 items-start p-2.5 mb-4 bg-red-50 rounded-lg border border-red-200">
                  <AlertTriangle className="flex-shrink-0 w-4 h-4 text-red-600 mt-0.5" />
                  <p className="text-xs sm:text-sm text-red-800">
                    The provider will be notified of this rejection and the reason provided.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 sm:gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleReject}
                    disabled={isSubmitting || !reason.trim()}
                    className="flex flex-1 justify-center items-center px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white" />
                    ) : (
                      <>
                        <XCircle className="mr-1.5 w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline">Reject Policy</span>
                        <span className="sm:hidden">Reject</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
